import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, ShieldAlert, Upload, Camera, FileText, IdCard, MapPin, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import CameraCapture from './CameraCapture';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerificationCard() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/verification/my`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const uploadFile = async (docType, file) => {
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append('doc_type', docType);
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/verification/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Upload failed');
      }
      toast.success(`${docType.replace('_', ' ')} uploaded!`);
      fetchStatus();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(null);
    }
  };

  const handleFileInput = (docType) => (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(docType, file);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/verification/submit`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Submit failed');
      }
      toast.success('Submitted for review!');
      fetchStatus();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return null;

  const statusConfig = {
    unverified: { color: 'bg-slate-100 text-slate-700', icon: ShieldAlert, label: 'Not Verified' },
    pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Under Review' },
    verified: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Verified' },
    rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' }
  };
  const s = statusConfig[data.status];
  const StatusIcon = s.icon;

  const allUploaded = data.id_document_uploaded && data.selfie_uploaded && data.address_proof_uploaded;
  const canEdit = data.status === 'unverified' || data.status === 'rejected';

  const DocRow = ({ label, uploaded, icon: Icon, docType, isSelfie = false, accept = "image/*,application/pdf" }) => (
    <div data-testid={`doc-row-${docType}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
        uploaded ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-500'
      }`}>
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 text-sm">{label}</p>
        <p className="text-xs text-slate-500">
          {uploaded ? '✓ Uploaded' : 'Not uploaded'}
        </p>
      </div>
      {canEdit && (
        isSelfie ? (
          <Button
            data-testid={`upload-${docType}-btn`}
            onClick={() => setShowCamera(true)}
            disabled={uploading === docType}
            className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full px-4 py-1.5 text-xs font-bold btn-hover"
          >
            {uploading === docType ? '...' : uploaded ? 'Retake' : 'Take Selfie'}
          </Button>
        ) : (
          <label className="cursor-pointer">
            <input
              data-testid={`upload-${docType}-input`}
              type="file"
              accept={accept}
              onChange={handleFileInput(docType)}
              className="hidden"
            />
            <span className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full px-4 py-1.5 text-xs font-bold inline-flex items-center gap-1">
              <Upload className="w-3 h-3" /> {uploading === docType ? '...' : uploaded ? 'Change' : 'Upload'}
            </span>
          </label>
        )
      )}
    </div>
  );

  return (
    <>
      <Card data-testid="verification-card" className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-slate-900" />
            <h3 className="font-bold text-lg text-slate-900">Verification</h3>
          </div>
          <div data-testid="verification-status-badge" className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${s.color}`}>
            <StatusIcon className="w-3.5 h-3.5" /> {s.label}
          </div>
        </div>

        {data.status === 'verified' && (
          <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3 mb-4">
            Your account is verified. You can accept jobs.
          </p>
        )}
        {data.status === 'pending' && (
          <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3 mb-4">
            Documents submitted. Our team is reviewing — you'll be notified when approved.
          </p>
        )}
        {data.status === 'rejected' && data.rejection_reason && (
          <p className="text-sm text-red-700 bg-red-50 rounded-lg p-3 mb-4">
            <strong>Rejected:</strong> {data.rejection_reason}
          </p>
        )}
        {data.status === 'unverified' && (
          <p className="text-sm text-slate-600 mb-4">
            Upload the 3 documents below to start accepting errands.
          </p>
        )}

        <div className="space-y-2 mb-4">
          <DocRow label="ID Copy" uploaded={data.id_document_uploaded} icon={IdCard} docType="id_document" />
          <DocRow label="Selfie (Face Photo)" uploaded={data.selfie_uploaded} icon={Camera} docType="selfie" isSelfie />
          <DocRow label="Proof of Address" uploaded={data.address_proof_uploaded} icon={MapPin} docType="address_proof" />
        </div>

        {canEdit && allUploaded && (
          <Button
            data-testid="submit-verification-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-slate-900 hover:bg-black text-white rounded-full py-3 font-bold btn-hover"
          >
            {submitting ? 'Submitting...' : 'Submit for Review'}
          </Button>
        )}
      </Card>

      {showCamera && (
        <CameraCapture
          onCapture={(file) => { setShowCamera(false); uploadFile('selfie', file); }}
          onCancel={() => setShowCamera(false)}
        />
      )}
    </>
  );
}
